// src/hooks/useCalendarExport.js
import { useCallback } from "react";

// Helper für 2-stelliges Padding
function pad(n) {
  return n.toString().padStart(2, "0");
}

// Helper für UTC-ICS Datumsformat (20250707T080000Z)
function toUTCString(d) {
  return (
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

// Haupt-Generator für Outlook/Apple kompatibles .ics
function generateICS({ title, date, location, description }) {
  const dt = date instanceof Date ? date : new Date(date);
  if (isNaN(dt)) return "";
  const dtStart = toUTCString(dt);
  const dtEnd = toUTCString(new Date(dt.getTime() + 60 * 60 * 1000)); // 1h Termin
  const uid = `${Math.random().toString(36).slice(2)}@bitterlerneinfach.de`;

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//BitterLerneinfach//DE",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `SUMMARY:${title}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    "STATUS:CONFIRMED",
    location ? `LOCATION:${location}` : "",
    `DESCRIPTION:${description || ""}`,
    "SEQUENCE:0",
    "TRANSP:OPAQUE",
    "END:VEVENT",
    "END:VCALENDAR"
  ].filter(Boolean).join("\r\n");
}

// Google-Kalender-Link
function createGoogleCalendarLink({ title, date, location, description }) {
  const dt = date instanceof Date ? date : new Date(date);
  if (isNaN(dt)) return "#";
  const start = toUTCString(dt);
  const end = toUTCString(new Date(dt.getTime() + 60 * 60 * 1000));
  const text = encodeURIComponent(title);
  const details = encodeURIComponent(description || "");
  const loc = encodeURIComponent(location || "");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${start}/${end}&details=${details}&location=${loc}`;
}

export default function useCalendarExport() {
  // Download .ics Datei
  const downloadICS = useCallback(
    ({ title, date, location, description }) => {
      const ics = generateICS({ title, date, location, description });
      const blob = new Blob([ics], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title.replace(/\s+/g, "_")}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    []
  );

  // Google Kalender-Link
  const getGoogleCalendarLink = useCallback(
    ({ title, date, location, description }) =>
      createGoogleCalendarLink({ title, date, location, description }),
    []
  );

  return { downloadICS, getGoogleCalendarLink };
}
